
import { useState, useEffect } from 'react';
import { User, GameDurationType } from '@/types';
import { useAuth } from '@/context/AuthContext';
import { createGame } from '@/integrations/supabase/game';
import { toast } from 'sonner';

interface UseCreateGameProps {
  onCreateGame: () => void;
  defaultCourseId?: string;
  defaultSelectedFriends?: User[];
}

export const useCreateGame = ({
  onCreateGame,
  defaultCourseId,
  defaultSelectedFriends = []
}: UseCreateGameProps) => {
  const { user } = useAuth();
  const [name, setName] = useState<string>('');
  const [courseId, setCourseId] = useState<string>(defaultCourseId || '');
  const [bet, setBet] = useState<string>('');
  const [selectedFriends, setSelectedFriends] = useState<User[]>(defaultSelectedFriends || []);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [durationType, setDurationType] = useState<GameDurationType>('yearly');
  const [startDate, setStartDate] = useState<string>(new Date().toISOString());
  const [endDate, setEndDate] = useState<string | null>(null);
  const [isRecurring, setIsRecurring] = useState<boolean>(false);
  const [courseError, setCourseError] = useState<string>('');
  const [nameError, setNameError] = useState<string>('');
  
  // Auto-compute dates when durationType changes
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    switch (durationType) {
      case 'yearly':
        setStartDate(`${currentYear}-01-01T00:00:00.000Z`);
        setEndDate(`${currentYear}-12-31T23:59:59.999Z`);
        break;
      case 'custom':
        setStartDate(new Date().toISOString());
        setEndDate(null);
        break;
      case 'open_ended':
        setStartDate(new Date().toISOString());
        setEndDate(null);
        break;
    }
  }, [durationType]);

  // Add current user to selected players if not already included
  useEffect(() => {
    if (user) {
      const currentUser: User = {
        id: user.id,
        name: user.user_metadata?.name || user.email?.split('@')[0] || 'Me',
        email: user.email,
      };
      setSelectedFriends(prev => {
        if (prev.some(f => f.id === user.id)) return prev;
        return [currentUser, ...prev];
      });
    }
  }, [user]);

  // Set default course if provided
  useEffect(() => {
    if (defaultCourseId && !courseId) {
      setCourseId(defaultCourseId);
    }
  }, [defaultCourseId]);
  
  const handleGolferSelect = (golfer: User) => {
    setSelectedFriends(prev => [...prev, golfer]);
  };
  
  const handleGolferRemove = (golferId: string) => {
    // If trying to deselect current user, prevent it
    if (user && golferId === user.id) {
      toast.error("You can't remove yourself from the game");
      return;
    }
    
    setSelectedFriends(prev => prev.filter(friend => friend.id !== golferId));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset errors
    setCourseError('');
    setNameError('');
    
    // Validate
    let isValid = true;
    
    if (!name.trim()) {
      setNameError('Name is required');
      isValid = false;
    }
    
    if (!courseId) {
      setCourseError('Please select a course');
      isValid = false;
    }
    
    if (!isValid) return;
    
    setIsSubmitting(true);
    
    try {
      const recurringGroupId = isRecurring ? crypto.randomUUID() : undefined;

      const result = await createGame({
        name,
        courseId,
        players: selectedFriends,
        bet: bet || undefined,
        durationType,
        isRecurring,
        recurringGroupId,
        startDate,
        endDate,
      });
      
      if (result.error) {
        console.error("Error in game creation:", result.error);
        toast.error("Failed to create game", {
          description: "There was an error creating your game. Please try again."
        });
      } else {
        toast.success("Game Created", {
          description: "Your new birdie game has been created."
        });
        onCreateGame();
      }
    } catch (error) {
      console.error("Error creating game:", error);
      toast.error("Failed to create game", {
        description: "There was an error creating your game. Please try again."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    name,
    setName,
    courseId,
    setCourseId,
    bet,
    setBet,
    selectedFriends,
    isSubmitting,
    durationType,
    setDurationType,
    isRecurring,
    setIsRecurring,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    courseError,
    nameError,
    handleGolferSelect,
    handleGolferRemove,
    handleSubmit
  };
};
